/**
 * Visitor region for pricing: India vs international.
 * Single sessionStorage key + deduped fetch so App and PricingPage can share one lookup.
 */

import { maybeTrackVisitorCountryAfterGeo } from './userJourneyTracker';

export const VISITOR_INDIA_SESSION_KEY = 'makereels_visitor_is_india_v1';

/**
 * @returns {boolean | null} null if not cached yet
 */
export function readCachedVisitorIsIndia() {
  try {
    const raw = sessionStorage.getItem(VISITOR_INDIA_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed.is_india === 'boolean') return parsed.is_india;
  } catch (_) {
    /* ignore */
  }
  return null;
}

/**
 * @returns {string | null} uppercased 2-letter code when cached
 */
export function readCachedVisitorCountryCode() {
  try {
    const raw = sessionStorage.getItem(VISITOR_INDIA_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const cc = parsed.country_code;
    if (cc == null) return null;
    const s = String(cc).trim().toUpperCase();
    return s.length === 2 && /^[A-Z]{2}$/.test(s) ? s : null;
  } catch (_) {
    return null;
  }
}

function writeCachedVisitorIsIndia(isIndia, countryCode = null) {
  try {
    const payload = { is_india: isIndia };
    if (countryCode != null) {
      const s = String(countryCode).trim().toUpperCase();
      if (s.length === 2 && /^[A-Z]{2}$/.test(s)) {
        payload.country_code = s;
      }
    }
    sessionStorage.setItem(VISITOR_INDIA_SESSION_KEY, JSON.stringify(payload));
  } catch (_) {
    /* ignore */
  }
}

/** In-flight promise so parallel callers share one /api/visitor-country request */
let inflightPrefetch = null;

/**
 * Ensures sessionStorage has visitor India flag. Call from App on load; PricingPage syncs state from result.
 * @returns {Promise<boolean>}
 */
export function prefetchVisitorCountry() {
  const cached = readCachedVisitorIsIndia();
  if (cached !== null) {
    const cc = readCachedVisitorCountryCode();
    if (cc) {
      maybeTrackVisitorCountryAfterGeo({ country_code: cc });
    }
    return Promise.resolve(cached);
  }
  if (inflightPrefetch) {
    return inflightPrefetch;
  }
  inflightPrefetch = fetch('/api/visitor-country')
    .then((res) => (res.ok ? res.json() : {}))
    .then((data) => {
      const isIndia = data.is_india !== false;
      const rawCc = data.country_code != null ? String(data.country_code).trim().toUpperCase() : '';
      const validCc = rawCc.length === 2 && /^[A-Z]{2}$/.test(rawCc) ? rawCc : null;
      writeCachedVisitorIsIndia(isIndia, validCc);
      if (validCc) {
        maybeTrackVisitorCountryAfterGeo({ country_code: validCc });
      }
      return isIndia;
    })
    .catch(() => {
      /* keep India as safe default; do not cache failures so a later retry can succeed */
      return true;
    })
    .finally(() => {
      inflightPrefetch = null;
    });
  return inflightPrefetch;
}
